import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  DollarSign, 
  RefreshCcw, 
  TrendingUp, 
  ChevronRight, 
  Zap, 
  Clock, 
  Calendar,
  AlertCircle,
  BarChart3,
  Layers,
  Bot,
  Sparkles,
  ArrowDownLeft,
  ArrowUpRight,
  Building2,
  Search,
} from 'lucide-react';
import { cn } from '../lib/utils';

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// ============== Shared Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const StatCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string; subText?: string }> = 
  ({ label, value, icon, color, subText }) => (
    <motion.div 
      variants={fadeInUp}
      className="glass-card p-6 border-l-4 relative overflow-hidden group"
      style={{ borderLeftColor: color }}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{label}</div>
          <div className="text-2xl font-bold text-white tracking-tight">{value}</div>
          {subText && <div className="text-[10px] text-slate-500 mt-2 font-medium">{subText}</div>}
        </div>
        <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-white transition-colors border border-white/5">
          {icon}
        </div>
      </div>
    </motion.div>
  );

const CashManagementScreen: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'ladder' | 'flows'>('overview');
  const [loading, setLoading] = useState(true);
  const [liquidity, setLiquidity] = useState<any>(null);
  const [recommendations, setRecommendations] = useState<any[]>([]);
  const [ladder, setLadder] = useState<any>(null);
  const [flows, setFlows] = useState<any[]>([]);
  const [flowSearch, setFlowSearch] = useState('');
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<number | null>(null);
  const [portfolios, setPortfolios] = useState<any[]>([]);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/cash/dashboard', {
        params: selectedPortfolioId ? { portfolioId: selectedPortfolioId } : {}
      });
      setLiquidity(response.data.liquidity);
      setRecommendations(response.data.recommendations || []);
    } catch (error) {
      console.error('Failed to fetch cash dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedPortfolioId]);

  useEffect(() => {
    fetchPortfolios();
  }, []);

  useEffect(() => {
    fetchDashboard();
    if (selectedPortfolioId) {
      if (activeTab === 'ladder') fetchLadder(selectedPortfolioId);
      if (activeTab === 'flows') fetchFlows(selectedPortfolioId);
    }
  }, [selectedPortfolioId, activeTab, fetchDashboard]);

  const fetchPortfolios = async () => {
    try {
      const response = await axios.get('/api/dimensions/portfolios');
      const data = response.data?.content || response.data || [];
      setPortfolios(data);
      if (data.length > 0) setSelectedPortfolioId(data[0].id);
    } catch (error) {
      console.error('Failed to fetch portfolios:', error);
    }
  };

  const fetchLadder = async (id: number) => {
    try {
      const res = await axios.get(`/api/cash/ladder/${id}?daysAhead=30`);
      setLadder(res.data);
    } catch (error) { console.error(error); }
  };

  const fetchFlows = async (id: number) => {
    try {
      const res = await axios.get(`/api/cash/movements/${id}`, {
        params: { daysBack: 365, daysAhead: 365 }
      });
      setFlows(res.data);
    } catch (error) { console.error(error); }
  };

  const formatCurrency = (val: number) => {
    if (Math.abs(val) >= 1000000) return `$${(val / 1000000).toFixed(2)}M`;
    if (Math.abs(val) >= 1000) return `$${(val / 1000).toFixed(1)}K`;
    return `$${val.toFixed(2)}`;
  };

  const filteredFlows = flows.filter((f) => {
    const q = flowSearch.trim().toLowerCase();
    if (!q) return true;
    return [
      f.cusip,
      f.securityName,
      f.security_name,
      f.movementType,
      f.movement_type,
      f.transactionType,
      f.transaction_type,
      f.eventType,
      f.event_type,
      f.description,
      f.referenceId,
      f.reference_id,
      f.tradeId,
      f.trade_id
    ].some((value) => String(value ?? '').toLowerCase().includes(q));
  });

  return (
    <Container>
      <div className="max-w-[1800px] mx-auto space-y-8">
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <DollarSign className="w-8 h-8 text-emerald-500" />
                Liquidity Command
              </h1>
              <p className="text-slate-500 font-medium mt-1">Surveillance of available cash, projected flows, and funding requirements</p>
            </div>
            <div className="flex gap-3">
              <div className="flex bg-slate-900 border border-white/10 p-1.5 rounded-xl">
                <select 
                  value={selectedPortfolioId || ''} 
                  onChange={(e) => setSelectedPortfolioId(Number(e.target.value))}
                  className="bg-transparent text-xs font-bold text-slate-300 px-3 focus:outline-none cursor-pointer"
                >
                  {portfolios.map(p => <option key={p.id} value={p.id} className="bg-slate-900">{p.portfolioName || p.name}</option>)}
                </select>
              </div>
              <button 
                onClick={fetchDashboard}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all"
              >
                <RefreshCcw className={cn("w-4 h-4", loading && "animate-spin")} />
                Sync Flows
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard label="Total Cash On-Hand" value={formatCurrency(liquidity?.totalAvailableCash || 0)} icon={<DollarSign className="w-5 h-5" />} color="#10b981" subText={`Across ${liquidity?.portfolioCount || 0} portfolios`} />
            <StatCard label="30D Projected Net" value={formatCurrency(liquidity?.netProjectedFlow30d || 0)} icon={<TrendingUp className="w-5 h-5" />} color="#3b82f6" subText={`Inflows: ${formatCurrency(liquidity?.projectedInflows30d || 0)}`} />
            <StatCard label="Funding Alerts" value={String((liquidity?.portfoliosBelowMinimum || 0) + (liquidity?.portfoliosAboveMaximum || 0))} icon={<AlertCircle className="w-5 h-5" />} color={liquidity?.portfoliosBelowMinimum > 0 ? "#ef4444" : "#f59e0b"} subText={`${liquidity?.portfoliosBelowMinimum || 0} critical deficits`} />
            <StatCard label="Pending Settlements" value={formatCurrency(liquidity?.totalPendingSettlements || 0)} icon={<Clock className="w-5 h-5" />} color="#8b5cf6" subText={`Reserved: ${formatCurrency(liquidity?.totalReservedCash || 0)}`} />
          </div>
        </motion.div>

        <div className="flex items-center border-b border-white/5 mb-6">
          {[
            { id: 'overview', label: 'Cash Positions', icon: <Layers className="w-4 h-4" /> },
            { id: 'ladder', label: 'Cash Ladder', icon: <BarChart3 className="w-4 h-4" /> },
            { id: 'flows', label: 'Flow History', icon: <Calendar className="w-4 h-4" /> }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={cn(
                "group flex items-center gap-2.5 px-6 py-3 text-sm font-semibold transition-all duration-200 relative",
                activeTab === tab.id ? "text-white" : "text-slate-400 hover:text-slate-200"
              )}
            >
              <span className={cn(activeTab === tab.id ? "text-blue-400" : "text-slate-500 group-hover:text-slate-300")}>{tab.icon}</span>
              {tab.label}
              {activeTab === tab.id && <motion.div layoutId="cash-tab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)]" />}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div key="overview" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <GlassCard className="lg:col-span-2 overflow-hidden">
                <div className="p-6 border-b border-white/5 bg-slate-950/30 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2"><Building2 className="w-5 h-5 text-blue-400" /> Portfolio Liquidity Grid</h3>
                  <button className="text-xs font-bold text-blue-400 hover:text-blue-300 uppercase tracking-widest transition-colors">Export CSV</button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5 bg-slate-950/20">
                        <th className="py-4 px-6">Portfolio</th>
                        <th className="py-4 px-6">Available</th>
                        <th className="py-4 px-6">Reserved</th>
                        <th className="py-4 px-6">Net Liquid</th>
                        <th className="py-4 px-6">Min. Threshold</th>
                        <th className="py-4 px-6 text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm">
                      {(liquidity?.portfolios || []).map((p: any, i: number) => (
                        <tr key={i} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                          <td className="py-4 px-6 font-bold text-white">{p.portfolioName}</td>
                          <td className="py-4 px-6 font-mono text-slate-300">{formatCurrency(p.availableCash)}</td>
                          <td className="py-4 px-6 font-mono text-slate-500">{formatCurrency(p.reservedCash)}</td>
                          <td className="py-4 px-6 font-mono font-bold text-emerald-400">{formatCurrency(p.netAvailable)}</td>
                          <td className="py-4 px-6 font-mono text-slate-500">{formatCurrency(p.minBalance)}</td>
                          <td className="py-4 px-6 text-right">
                            <span className={cn(
                              "px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider",
                              p.status === 'OK' ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-red-500/10 text-red-400 border border-red-500/20"
                            )}>{p.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </GlassCard>

              <div className="space-y-6">
                <GlassCard className="p-6">
                  <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2"><Zap className="w-5 h-5 text-amber-400" /> Critical Funding Alerts</h3>
                  <div className="space-y-4">
                    {recommendations.length > 0 ? recommendations.map((rec, i) => (
                      <div key={i} className={cn(
                        "p-4 rounded-2xl border flex gap-4",
                        rec.priority === 'HIGH' ? "bg-red-500/5 border-red-500/20" : "bg-amber-500/5 border-amber-500/20"
                      )}>
                        <div className={cn(
                          "w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
                          rec.priority === 'HIGH' ? "bg-red-500/20 text-red-400" : "bg-amber-500/20 text-amber-400"
                        )}>
                          <AlertCircle className="w-5 h-5" />
                        </div>
                        <div>
                          <div className="text-sm font-bold text-white">{rec.portfolioName}</div>
                          <div className="text-xs text-slate-400 mt-1 leading-relaxed">{rec.reason}</div>
                          <div className="text-[10px] font-bold text-blue-400 uppercase tracking-widest mt-2">{rec.action}</div>
                        </div>
                      </div>
                    )) : (
                      <div className="py-12 text-center text-slate-500 italic text-sm">No funding alerts active</div>
                    )}
                  </div>
                </GlassCard>

                <GlassCard className="p-6 bg-gradient-to-br from-indigo-600/20 to-violet-600/20 border-indigo-500/30 group">
                  <div className="flex justify-between items-start mb-4">
                    <div className="w-10 h-10 rounded-xl bg-indigo-500/20 flex items-center justify-center text-indigo-400">
                      <Bot className="w-6 h-6" />
                    </div>
                    <Sparkles className="w-4 h-4 text-amber-400" />
                  </div>
                  <h4 className="font-bold text-white mb-1">Cash Flow Optimizer</h4>
                  <p className="text-xs text-slate-400 leading-relaxed mb-4">Let Axiom AI analyze settlement cycles and suggest funding movements across custody accounts.</p>
                  <button className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-lg transition-all shadow-lg shadow-indigo-600/20 uppercase tracking-widest">Analyze Sweep Opportunities</button>
                </GlassCard>
              </div>
            </motion.div>
          )}

          {activeTab === 'ladder' && (
            <motion.div key="ladder" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <GlassCard className="p-8">
                <div className="flex items-center justify-between mb-8">
                  <h3 className="text-xl font-bold text-white flex items-center gap-3"><BarChart3 className="w-6 h-6 text-blue-400" /> 30-Day Liquidity Projection</h3>
                  <div className="flex gap-4">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-emerald-500" />
                      <span className="text-[10px] font-bold text-slate-500 uppercase">Above Min</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500" />
                      <span className="text-[10px] font-bold text-slate-500 uppercase">Deficit Risk</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  {ladder?.ladder?.slice(0, 20).map((day: any, i: number) => {
                    const max = ladder.maxBalance || 1;
                    const pct = (day.endingBalance / max) * 100;
                    return (
                      <div key={i} className="flex items-center gap-6 group">
                        <div className="w-24 text-xs font-bold text-slate-500 group-hover:text-slate-300 transition-colors">
                          {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </div>
                        <div className="flex-1 h-8 bg-slate-900/50 rounded-lg overflow-hidden border border-white/5 relative">
                          <motion.div 
                            initial={{ width: 0 }}
                            animate={{ width: `${Math.max(5, pct)}%` }}
                            className={cn(
                              "h-full transition-all shadow-lg",
                              day.belowMinimum ? "bg-gradient-to-r from-red-600 to-orange-600" : "bg-gradient-to-r from-emerald-600 to-blue-600"
                            )}
                          />
                          <div className="absolute inset-0 flex items-center px-3 justify-end pointer-events-none">
                            <span className="text-[10px] font-mono font-bold text-white">{formatCurrency(day.endingBalance)}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </GlassCard>
            </motion.div>
          )}

          {activeTab === 'flows' && (
            <motion.div key="flows" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
              <GlassCard>
                <div className="p-6 border-b border-white/5 bg-slate-950/30 flex items-center justify-between">
                  <h3 className="text-lg font-bold text-white flex items-center gap-2"><Calendar className="w-5 h-5 text-indigo-400" /> Posted Cash Movement Ledger</h3>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <input
                        value={flowSearch}
                        onChange={(e) => setFlowSearch(e.target.value)}
                        placeholder="Search CUSIP, trade, type..."
                        className="w-72 pl-9 pr-3 py-2 bg-slate-950/60 border border-white/10 rounded-xl text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500/70"
                      />
                    </div>
                    <button
                      onClick={() => selectedPortfolioId && fetchFlows(selectedPortfolioId)}
                      className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold rounded-xl transition-all border border-white/5"
                    >
                      Refresh Ledger
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5 bg-slate-950/20">
                        <th className="py-4 px-6">Event Date</th>
                        <th className="py-4 px-6">Source/Type</th>
                        <th className="py-4 px-6">Security</th>
                        <th className="py-4 px-6">Trade</th>
                        <th className="py-4 px-6">Amount</th>
                        <th className="py-4 px-6">Status</th>
                        <th className="py-4 px-6 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm">
                      {filteredFlows.map((f, i) => {
                        const eventDate = f.valueDate || f.value_date || f.flowDate;
                        const movementType = f.movementType || f.movement_type || f.flowType;
                        const securityName = f.securityName || f.security_name || f.security?.securityName || 'Portfolio Funding';
                        const tradeId = f.tradeId || f.trade_id || f.referenceId || f.reference_id;
                        const amount = Number(f.amount || 0);
                        const status = f.status || 'POSTED';
                        return (
                        <tr key={f.id || i} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                          <td className="py-4 px-6 font-medium text-slate-300">{eventDate ? new Date(eventDate).toLocaleDateString() : '-'}</td>
                          <td className="py-4 px-6">
                            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-slate-800 text-slate-400 border border-white/5">{movementType}</span>
                          </td>
                          <td className="py-4 px-6">
                            <div className="font-bold text-white">{securityName}</div>
                            <div className="text-[10px] font-mono text-blue-400">{f.cusip || '-'}</div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="text-xs font-bold text-slate-300">{tradeId ? `#${tradeId}` : '-'}</div>
                            <div className="text-[10px] text-slate-500">{f.transactionType || f.transaction_type || f.eventType || f.event_type || ''}</div>
                          </td>
                          <td className={cn("py-4 px-6 font-mono font-bold", amount >= 0 ? "text-emerald-400" : "text-red-400")}>
                            {amount >= 0 ? <ArrowDownLeft className="w-3 h-3 inline mr-1" /> : <ArrowUpRight className="w-3 h-3 inline mr-1" />}
                            {formatCurrency(amount)}
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-2">
                              <div className={cn("w-1.5 h-1.5 rounded-full", status === 'PENDING' || status === 'PROJECTED' ? "bg-blue-400" : "bg-emerald-400")} />
                              <span className="text-[10px] font-bold text-slate-400 uppercase">{status}</span>
                            </div>
                          </td>
                          <td className="py-4 px-6 text-right">
                            <button className="p-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-white transition-all border border-white/5"><ChevronRight className="w-4 h-4" /></button>
                          </td>
                        </tr>
                      );})}
                      {filteredFlows.length === 0 && (
                        <tr>
                          <td colSpan={7} className="py-12 text-center text-slate-500 italic">No cash movements found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </GlassCard>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Container>
  );
};

export default CashManagementScreen;
